import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useState } from "react";
import { ApplicationFlowState } from "src/apps/AppFlow/screens/root/Layout";
import IdVerificationDecision from "src/apps/AppFlow/screens/root/Layout/sections/IdentityVerification/pages/IdVerificationDecision";
import IdVerificationPreamble from "src/apps/AppFlow/screens/root/Layout/sections/IdentityVerification/pages/IdVerificationPreamble";
import IdVerificationQuestion from "src/apps/AppFlow/screens/root/Layout/sections/IdentityVerification/pages/IdVerificationQuestion";
import { useIdentityVerificationQuestionsWithContext } from "src/apps/AppFlow/screens/root/Layout/sections/IdentityVerification/query";
import { Page } from "src/components/FlowLayout";
import { PageStateConverter } from "src/hooks/usePageState";
import { ReducerReturn } from "src/hooks/useReducerPageState";

import { isTruthy } from "@fraction/shared";

export type IDVerificationPagesArguments = PageStateConverter<
  ApplicationFlowState,
  "idVerificationQuestions"
>;

export const useIdentityVerificationPages = ({
  state,
  dispatch,
  extract,
}: ReducerReturn<ApplicationFlowState>): Page[] => {
  const { idVerificationFlow } = useFlags();
  const { onNext, onBack } = extract("idVerificationQuestions");
  const idVerification = useIdentityVerificationQuestionsWithContext({ state, dispatch });
  const { questions, loading, decision } = idVerification;
  const [hasClickedStart, setHasClickedStart] = useState(false);

  const handleStart = useCallback(() => {
    setHasClickedStart(true);
    onNext?.();
  }, [onNext]);

  if (!idVerificationFlow) {
    return [];
  }

  return [
    (!decision || !hasClickedStart) && {
      name: "KIQ-Preamble",
      component: <IdVerificationPreamble nextLoading={loading} onNext={handleStart} onBack={onBack} />,
    },
    ...questions.map(
      (question, idx) =>
        (!decision || !hasClickedStart) && {
          name: `KIQ-${question.questionNumber}`,
          component: (
            <IdVerificationQuestion
              key={question.questionText}
              question={question}
              questionIndex={idx}
              state={state}
              dispatch={dispatch}
              extract={extract}
            />
          ),
        }
    ),
    {
      name: "KIQ-decision",
      component: (
        <IdVerificationDecision nextLoading={loading} value={decision} onBack={onBack} onNext={onNext} />
      ),
    },
  ].filter(isTruthy);
};
